import React, { useEffect, useState, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useLanguage } from "../../../contexts/LanguageContext";
import { useAuth } from "../../../contexts/AuthContext";
import api from "../../../api/client";
import logger from "../../../utils/logger";
import {
  Search,
  Grid,
  List,
  ChevronDown,
  ArrowLeft,
  ArrowRight,
  SlidersHorizontal,
  RefreshCcw,
  Home,
} from "lucide-react";
import ProductCard from "../../ProductCard/ProductCard";
import "./CategoryPage.css";

/* ─── helpers ─────────────────────────────────── */
function normalize(raw) {
  const name_en = raw.name_en || raw.title || raw.name || "No name";
  const name_ar = raw.name_ar || raw.title_ar || raw.name_en || name_en;
  const originalPrice = Number(
    raw.original_price ?? raw.old_price ?? raw.price ?? 0
  );
  const finalPrice = Number(
    raw.final_price ?? raw.sale_price ?? raw.price ?? 0
  );
  const hasDiscount =
    raw.has_discount === true ||
    (originalPrice > 0 && finalPrice > 0 && finalPrice < originalPrice);

  const imageSrc =
    raw.image_url ||
    (raw.image && /^https?:\/\//i.test(raw.image) ? raw.image : null);

  return {
    ...raw,
    name_en,
    name_ar,
    price: originalPrice > 0 ? originalPrice : 0,
    final_price: finalPrice > 0 ? finalPrice : originalPrice,
    sale_price: hasDiscount ? finalPrice : 0,
    old_price: hasDiscount ? originalPrice : Number(raw.old_price ?? 0),
    original_price: hasDiscount ? originalPrice : 0,
    has_discount: hasDiscount,
    imageSrc,
  };
}

/* ─── Skeleton ────────────────────────────────── */
function SkeletonGrid() {
  return (
    <>
      <div
        style={{
          height: 160,
          borderRadius: "var(--radius-xl)",
          background:
            "linear-gradient(90deg, var(--bg-elevated) 25%, var(--bg-primary) 50%, var(--bg-elevated) 75%)",
          backgroundSize: "200% 100%",
          animation: "catShimmer 1.6s infinite",
          marginBottom: "2rem",
        }}
      />
      <div className="cat-skeleton-grid">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="cat-skeleton-card">
            <div className="cat-skeleton-img" />
            <div className="cat-skeleton-body">
              <div className="cat-skeleton-line" />
              <div className="cat-skeleton-line short" />
              <div className="cat-skeleton-line price" />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

/* ─── Main Page ───────────────────────────────── */
export default function CategoryPage() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const isAr = lang === "ar";

  const initialName = location.state?.categoryName || slug || "";
  const categoryId = location.state?.categoryId;

  const [allProducts, setAllProducts] = useState([]);
  const [displayed, setDisplayed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [priceRange, setPriceRange] = useState({ min: "", max: "" });
  const [viewMode, setViewMode] = useState("grid");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError("");

        const res = await api.apiGet("products");
        const all = Array.isArray(res?.products) ? res.products : [];

        let filtered = all;

        if (categoryId) {
          filtered = all.filter(
            (p) => String(p.category_id) === String(categoryId)
          );
        } else if (slug) {
          filtered = all.filter(
            (p) =>
              p.category?.toLowerCase().includes(slug.toLowerCase()) ||
              p.category_slug?.toLowerCase() === slug.toLowerCase()
          );
        }

        if (!cancelled) setAllProducts(filtered.map(normalize));
      } catch (e) {
        logger.error("CategoryPage fetch error", e);
        if (!cancelled)
          setError(isAr ? "حدث خطأ أثناء تحميل المنتجات." : "Error loading products.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [slug, categoryId, isAr]);

  /* filter + sort */
  useEffect(() => {
    let result = [...allProducts];

    if (priceRange.min !== "")
      result = result.filter(
        (p) => (p.sale_price || p.price) >= Number(priceRange.min)
      );
    if (priceRange.max !== "")
      result = result.filter(
        (p) => (p.sale_price || p.price) <= Number(priceRange.max)
      );

    switch (sortBy) {
      case "price-low":
        result.sort((a, b) => (a.sale_price || a.price) - (b.sale_price || b.price));
        break;
      case "price-high":
        result.sort((a, b) => (b.sale_price || b.price) - (a.sale_price || a.price));
        break;
      case "popular":
        result.sort((a, b) => (b.reviews_count || 0) - (a.reviews_count || 0));
        break;
      case "newest":
      default:
        result.sort(
          (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
        );
    }

    setDisplayed(result);
  }, [allProducts, sortBy, priceRange]);

  const handleAddToCart = useCallback((product) => {
    logger.info("Add to cart", product.id);
  }, []);

  const clearFilters = () => {
    setPriceRange({ min: "", max: "" });
    setSortBy("newest");
  };

  const activeFilterCount =
    (priceRange.min !== "" ? 1 : 0) + (priceRange.max !== "" ? 1 : 0);

  /* ── LOADING ── */
  if (loading) {
    return (
      <div className="category-page" dir={isAr ? "rtl" : "ltr"}>
        <div className="container">
          <SkeletonGrid />
        </div>
      </div>
    );
  }

  /* ── ERROR ── */
  if (error) {
    return (
      <div className="category-page" dir={isAr ? "rtl" : "ltr"}>
        <div className="container">
          <div className="cat-error">
            <p className="cat-error-text">{error}</p>
            <button
              className="cat-retry-btn"
              onClick={() => window.location.reload()}
            >
              <RefreshCcw size={16} />
              <span>{isAr ? "إعادة المحاولة" : "Retry"}</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── MAIN ── */
  return (
    <div className="category-page" dir={isAr ? "rtl" : "ltr"}>
      <div className="container">

        {/* ── Hero Header ── */}
        <div className="cat-hero">
          <div className="cat-hero-deco" />
          <div className="cat-hero-deco-2" />

          <div className="cat-hero-left">
            <nav className="cat-breadcrumb">
              <button className="cat-breadcrumb-link" onClick={() => navigate("/")}>
                <Home size={13} style={{ marginBottom: -2 }} />{" "}
                {isAr ? "الرئيسية" : "Home"}
              </button>
              <span className="cat-breadcrumb-sep">
                {isAr ? <ArrowLeft size={12} /> : <ArrowRight size={12} />}
              </span>
              <span className="cat-breadcrumb-current">{initialName}</span>
            </nav>

            <h1 className="cat-hero-title">{initialName}</h1>
            <p className="cat-hero-subtitle">
              {isAr
                ? "تصفّح أحدث المنتجات في هذه الفئة"
                : "Browse the latest products in this category"}
            </p>
          </div>

          <div className="cat-hero-right">
            <div className="cat-count-badge">
              <span className="cat-count-dot" />
              {displayed.length} {isAr ? "منتج" : "Products"}
            </div>
          </div>
        </div>

        {/* ── Controls Bar ── */}
        <div className="cat-controls">
          <button
            className={`cat-filter-btn ${showFilters ? "active" : ""}`}
            onClick={() => setShowFilters((v) => !v)}
          >
            <SlidersHorizontal size={16} />
            <span>{isAr ? "الفلاتر" : "Filters"}</span>
            {activeFilterCount > 0 && (
              <span className="cat-filter-badge">{activeFilterCount}</span>
            )}
          </button>

          <div className="cat-sort-group">
            <label className="cat-sort-label">
              {isAr ? "ترتيب:" : "Sort:"}
            </label>
            <div className="cat-select-wrap">
              <select
                className="cat-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="newest">{isAr ? "الأحدث" : "Newest"}</option>
                <option value="popular">
                  {isAr ? "الأكثر مبيعًا" : "Best Selling"}
                </option>
                <option value="price-low">
                  {isAr ? "السعر: الأقل أولاً" : "Price: Low → High"}
                </option>
                <option value="price-high">
                  {isAr ? "السعر: الأعلى أولاً" : "Price: High → Low"}
                </option>
              </select>
              <ChevronDown size={15} className="cat-select-icon" />
            </div>
          </div>

          <span className="cat-results-count">
            {isAr
              ? `${displayed.length} من ${allProducts.length} منتج`
              : `${displayed.length} of ${allProducts.length} products`}
          </span>

          <div className="cat-view-toggle">
            <button
              className={`cat-view-btn ${viewMode === "grid" ? "active" : ""}`}
              onClick={() => setViewMode("grid")}
              aria-label={isAr ? "شبكي" : "Grid"}
            >
              <Grid size={17} />
            </button>
            <button
              className={`cat-view-btn ${viewMode === "list" ? "active" : ""}`}
              onClick={() => setViewMode("list")}
              aria-label={isAr ? "قائمة" : "List"}
            >
              <List size={17} />
            </button>
          </div>
        </div>

        {/* ── Filters Panel ── */}
        {showFilters && (
          <div className="cat-filters-panel">
            <div className="cat-filter-group">
              <label className="cat-filter-label">
                {isAr ? "نطاق السعر" : "Price Range"}
              </label>
              <div className="cat-price-inputs">
                <input
                  type="number"
                  className="cat-price-input"
                  placeholder={isAr ? "من" : "Min"}
                  value={priceRange.min}
                  onChange={(e) =>
                    setPriceRange((p) => ({ ...p, min: e.target.value }))
                  }
                />
                <span className="cat-price-sep">—</span>
                <input
                  type="number"
                  className="cat-price-input"
                  placeholder={isAr ? "إلى" : "Max"}
                  value={priceRange.max}
                  onChange={(e) =>
                    setPriceRange((p) => ({ ...p, max: e.target.value }))
                  }
                />
              </div>
            </div>

            {activeFilterCount > 0 && (
              <button className="cat-clear-btn" onClick={clearFilters}>
                {isAr ? "مسح الكل" : "Clear All"}
              </button>
            )}
          </div>
        )}

        {/* ── Active filter tags ── */}
        {activeFilterCount > 0 && (
          <div className="cat-active-filters">
            {priceRange.min !== "" && (
              <span
                className="cat-active-tag"
                onClick={() => setPriceRange((p) => ({ ...p, min: "" }))}
              >
                {isAr ? `من $${priceRange.min}` : `Min $${priceRange.min}`}
                <span className="cat-tag-x">×</span>
              </span>
            )}
            {priceRange.max !== "" && (
              <span
                className="cat-active-tag"
                onClick={() => setPriceRange((p) => ({ ...p, max: "" }))}
              >
                {isAr ? `إلى $${priceRange.max}` : `Max $${priceRange.max}`}
                <span className="cat-tag-x">×</span>
              </span>
            )}
          </div>
        )}

        {/* ── Products ── */}
        {displayed.length > 0 ? (
          <div className={`cat-products-grid view-${viewMode}`}>
            {displayed.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
              />
            ))}
          </div>
        ) : (
          <div className="cat-empty">
            <Search className="cat-empty-icon"/>
            <h3>{isAr ? "لا توجد منتجات في هذه الفئة" : "No Products Found"}</h3>
            <p>
              {isAr
                ? "حاول تعديل الفلاتر أو البحث في فئة أخرى"
                : "Try adjusting your filters or browse another category"}
            </p>
            {activeFilterCount > 0 && (
              <button className="cat-empty-btn" onClick={clearFilters}>
                {isAr ? "مسح الفلاتر" : "Clear Filters"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
